import React, { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Link2 from "@mui/material/Link";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MainListItems from "./listItems";
import MenuPopup from "./menuPopup";
import { useSelector } from "react-redux";
import { logOut } from "../store/actions/commonAction";
import CircularProgress from "@mui/material/CircularProgress";

import Fab from "@mui/material/Fab";

import Logo from "../logo.jpg";

import SignIn from "./SignIn";
import Home from "./Home";
import NewsList from "./news/list";
import AddNews from "./news/add";
import GalleryList from "./gallery/list";
import AddGallery from "./gallery/add";
import VideosList from "./videos/list";
import ReviewsList from "./review/list";
import AddVideos from "./videos/add";
import RecentSalesList from "./recent_sales/list";
import AddRecentSales from "./recent_sales/add";
import FeaturedListingList from "./featured_listing/list";
import AddFeaturedListing from "./featured_listing/add";
import SoldListingList from "./sold_listing/list";
import AddSoldListing from "./sold_listing/add";
import FeaturedAgentList from "./featured_agent/list";
import AddFeaturedAgent from "./featured_agent/add";
import CareerList from "./career/list";
import AddCareer from "./career/add";
import DeveloperList from "./developer/list";
import AddDeveloper from "./developer/add";
import ShortCut from "./news/shortcut";
import HeaderImage from "./office/headerimage";
import SocialMediaLink from "./office/socialmedialink";

function Copyright(props) {
	return (
		<Typography
			variant="body2"
			color="text.secondary"
			align="center"
			{...props}
		>
			{"Copyright © "}
			<Link2 color="inherit" href="https://www.raywhite.co.id/">
				Ray White Indonesia
			</Link2>{" "}
			{new Date().getFullYear()}
			{"."}
		</Typography>
	);
}

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
	zIndex: theme.zIndex.drawer + 1,
	transition: theme.transitions.create(["width", "margin"], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginLeft: drawerWidth,
		width: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(["width", "margin"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

const Drawer = styled(MuiDrawer, {
	shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
	"& .MuiDrawer-paper": {
		position: "relative",
		whiteSpace: "nowrap",
		width: drawerWidth,
		transition: theme.transitions.create("width", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
		boxSizing: "border-box",
		...(!open && {
			overflowX: "hidden",
			transition: theme.transitions.create("width", {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			width: theme.spacing(7),
			[theme.breakpoints.up("sm")]: {
				width: theme.spacing(9),
			},
		}),
	},
}));

const mdTheme = createTheme();

function DashboardContent() {
	const [open, setOpen] = React.useState(true);
	const toggleDrawer = () => {
		setOpen(!open);
	};

	//const user = useSelector((state) => state.commonReducer.user);
	//const dispatch = useDispatch();

	const loading = useSelector((state) => state.commonReducer.loading);
	const module = useSelector((state) => state.commonReducer.module);
	const company = useSelector((state) => state.commonReducer.user.company);
	const office = useSelector((state) => state.commonReducer.user.office);

	useEffect(() => {
		document.title = "Ray White Indonesia - " + module;
	}, [module]);

	return (
		<ThemeProvider theme={mdTheme}>
			<Box sx={{ display: "flex" }}>
				{loading && (
					<Fab
						sx={{
							position: "fixed",
							top: "50%",
							right: "50%",
						}}
						color="default"
					>
						<CircularProgress />
					</Fab>
				)}
				<CssBaseline />
				<AppBar position="absolute" open={open}>
					<Toolbar
						variant="dense"
						sx={{
							pr: "24px", // keep right padding when drawer closed
						}}
					>
						<IconButton
							edge="start"
							color="inherit"
							aria-label="open drawer"
							onClick={toggleDrawer}
							sx={{
								marginRight: "36px",
								...(open && { display: "none" }),
							}}
						>
							<MenuIcon />
						</IconButton>
						<Typography
							component="h1"
							variant="h6"
							color="inherit"
							noWrap
							sx={{ flexGrow: 1 }}
						>
							{module}
						</Typography>

						<Typography
							component="div"
							variant="subtitle1"
							color="inherit"
						>
							{office}
						</Typography>

						<MenuPopup logOut={logOut} />
					</Toolbar>
				</AppBar>

				<Drawer variant="permanent" open={open}>
					<Toolbar
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-end",
							px: [1],
						}}
					>
						<Box
							component="img"
							sx={{
								height: 64,
							}}
							alt="Your logo."
							src={Logo}
						/>
						<IconButton onClick={toggleDrawer}>
							<ChevronLeftIcon />
						</IconButton>
					</Toolbar>
					<Divider />
					<List
						dense={true}
						component="nav"
						sx={{
							flexGrow: 1,
							height: "80vh",
							overflow: "hide",
						}}
					>
						<MainListItems
							module={module}
							company={company}
							logOut={logOut}
						/>
					</List>
				</Drawer>
				<Box
					component="main"
					sx={{
						backgroundColor: (theme) =>
							theme.palette.mode === "light"
								? theme.palette.grey[100]
								: theme.palette.grey[900],
						flexGrow: 1,
						height: "100vh",
						overflow: "auto",
					}}
				>
					<Toolbar />
					<Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
						<Routes>
							<Route path="/" element={<Home />} />

							<Route
								path="/news/:source"
								element={<NewsList />}
							/>
							<Route path="/addnews" element={<AddNews />} />
							<Route path="/editnews/:id" element={<AddNews />} />

							<Route path="/gallery" element={<GalleryList />} />
							<Route
								path="/addgallery"
								element={<AddGallery />}
							/>
							<Route
								path="/editgallery/:id"
								element={<AddGallery />}
							/>

							<Route path="/videos" element={<VideosList />} />
							<Route path="/addvideos" element={<AddVideos />} />
							<Route
								path="/editvideo/:id"
								element={<AddVideos />}
							/>

							<Route path="/reviews" element={<ReviewsList />} />

							<Route path="/career" element={<CareerList />} />
							<Route path="/addcareer" element={<AddCareer />} />
							<Route
								path="/editcareer/:id"
								element={<AddCareer />}
							/>

							<Route
								path="/developer"
								element={<DeveloperList />}
							/>
							<Route
								path="/editdeveloper/:id"
								element={<AddDeveloper />}
							/>

							<Route
								path="/recent_sales"
								element={<RecentSalesList />}
							/>
							<Route
								path="/addrecentsales"
								element={<AddRecentSales />}
							/>
							<Route
								path="/editrecentsales/:id"
								element={<AddRecentSales />}
							/>

							<Route
								path="/featured_listing"
								element={<FeaturedListingList />}
							/>
							<Route
								path="/addfeaturedlisting"
								element={<AddFeaturedListing />}
							/>
							<Route
								path="/editfeaturedlisting/:id"
								element={<AddFeaturedListing />}
							/>

							<Route
								path="/sold_listing"
								element={<SoldListingList />}
							/>
							<Route
								path="/addsoldlisting"
								element={<AddSoldListing />}
							/>
							<Route
								path="/editsoldlisting/:id"
								element={<AddSoldListing />}
							/>

							<Route
								path="/featured_agent"
								element={<FeaturedAgentList />}
							/>
							<Route
								path="/addfeaturedagent"
								element={<AddFeaturedAgent />}
							/>
							<Route
								path="/editfeaturedagent/:id"
								element={<AddFeaturedAgent />}
							/>

							<Route
								path="/shortcut/:source"
								element={<ShortCut />}
							/>
							<Route
								path="/headerimage"
								element={<HeaderImage />}
							/>
							<Route
								path="/socialmedialink"
								element={<SocialMediaLink />}
							/>
						</Routes>

						<Copyright sx={{ pt: 4 }} />
					</Container>
				</Box>
			</Box>
		</ThemeProvider>
	);
}

export default function Dashboard() {
	const isLoggedIn = useSelector((state) => state.commonReducer.isLoggedIn);
	if (isLoggedIn) {
		return <DashboardContent />;
	} else {
		return <SignIn />;
	}
}
